// core/tools/PenTool.ts — Pen tool for creating bezier paths
import type { Tool, EditorPointerEvent, HitResult, Point } from './types';
import type { Editor } from '../Editor';
import { ADD_LAYER, SELECT_LAYER } from '../commands/commands';
import { pathPointsBounds } from './pathUtils';
import type { PathPoint } from './pathUtils';

type PenMode = 'idle' | 'drawing' | 'dragging';

const CLOSE_THRESHOLD = 10; // pixels — how close to first point to trigger close

export class PenTool implements Tool {
  name = 'pen';

  private mode: PenMode = 'idle';
  private points: PathPoint[] = [];
  private dragStartPoint: Point | null = null;
  private lastClickTime = 0;

  /** Called after a path is finished and the new layer is selected. Use to switch back to select tool. */
  onFinish?: () => void;

  constructor(private editor: Editor) {}

  onPointerDown(event: EditorPointerEvent, _hit: HitResult): void {
    const now = Date.now();
    const isDoubleClick = now - this.lastClickTime < 300;
    this.lastClickTime = now;

    // Double-click finishes the path (open)
    if (isDoubleClick && this.points.length >= 2) {
      // Remove the last point added by the first click of the double-click
      this.points.pop();
      this.finishPath(false);
      return;
    }

    if (this.mode === 'idle' || this.mode === 'drawing') {
      // Check if clicking near the first point to close
      if (this.points.length >= 3) {
        const first = this.points[0];
        const dx = event.point.x - first.x;
        const dy = event.point.y - first.y;
        if (Math.sqrt(dx * dx + dy * dy) < CLOSE_THRESHOLD) {
          this.finishPath(true);
          return;
        }
      }

      // Add a new point
      this.points.push({ x: event.point.x, y: event.point.y });
      this.mode = 'dragging';
      this.dragStartPoint = event.point;
    }
  }

  onPointerMove(event: EditorPointerEvent): void {
    if (this.mode === 'dragging' && this.dragStartPoint && this.points.length > 0) {
      const current = this.points[this.points.length - 1];
      const dx = event.point.x - this.dragStartPoint.x;
      const dy = event.point.y - this.dragStartPoint.y;

      // Pull out bezier handles
      current.handleOut = { x: dx, y: dy };
      current.handleIn = { x: -dx, y: -dy };
    }
    // Preview state is read by the Compositor via getPreviewState()
  }

  onPointerUp(_event: EditorPointerEvent): void {
    if (this.mode === 'dragging') {
      this.mode = 'drawing';
      this.dragStartPoint = null;
    }
  }

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  /** Cancel the current path */
  cancel(): void {
    this.reset();
  }

  /** Finish and create the path layer */
  private finishPath(closed: boolean): void {
    if (this.points.length < 2) {
      this.reset();
      return;
    }

    const bounds = pathPointsBounds(this.points);

    // Normalize points relative to bounding box origin
    const normalizedPoints = this.points.map((p) => ({
      ...p,
      x: p.x - bounds.x,
      y: p.y - bounds.y,
    }));
    const width = Math.max(bounds.width, 1);
    const height = Math.max(bounds.height, 1);

    const id = `path-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

    this.editor.batch(() => {
      this.editor.dispatchCommand(ADD_LAYER, {
        id,
        type: 'shape',
        name: `Path ${Object.keys(this.editor.getState().layers).length}`,
        shape: 'path',
        pathPoints: normalizedPoints,
        pathClosed: closed,
        fill: '',
        stroke: '#000000',
        strokeWidth: 2,
        width,
        height,
        x: bounds.x + width / 2,
        y: bounds.y + height / 2,
      });
      this.editor.dispatchCommand(SELECT_LAYER, { layerId: id });
    });

    this.reset();
    this.onFinish?.();
  }

  private reset(): void {
    this.points = [];
    this.mode = 'idle';
    this.dragStartPoint = null;
  }

  // ─── Preview state for the Compositor to read ───────────

  getPoints(): PathPoint[] {
    return this.points;
  }

  getMode(): PenMode {
    return this.mode;
  }

  isNearFirstPoint(point: Point): boolean {
    if (this.points.length < 3) return false;
    const first = this.points[0];
    const dx = point.x - first.x;
    const dy = point.y - first.y;
    return Math.sqrt(dx * dx + dy * dy) < CLOSE_THRESHOLD;
  }
}
